import { Badge, formatDateTime, useDismissableOverlay } from '@catering/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { type JSX, useState } from 'react';

import { fetchNotifications, fetchUnreadCount, markNotificationRead } from '../notifications/api';

const UNREAD_POLL_INTERVAL_MS = 30_000;

/**
 * `notifications-module/design.md` §12: same pattern as `UserMenu` —
 * `useDismissableOverlay` for the popover, `Badge` for the counter, no new
 * component added to `@catering/ui` for a single consumer.
 */
export function NotificationBell(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { containerRef } = useDismissableOverlay({ open, onClose: () => setOpen(false) });
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: UNREAD_POLL_INTERVAL_MS,
  });

  const list = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => fetchNotifications(10, 0),
    enabled: open,
  });

  const count = unread.data?.count ?? 0;

  async function handleMarkRead(id: string): Promise<void> {
    await markNotificationRead(id);
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Notificaciones"
        className="relative flex size-9 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100"
      >
        <Bell aria-hidden="true" className="size-5" />
        {count > 0 ? (
          <span className="absolute -right-0.5 -top-0.5">
            <Badge variant="danger" className="min-w-5 justify-center px-1 text-[11px]">
              {count > 99 ? '99+' : count}
            </Badge>
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Notificaciones"
          className="absolute right-0 z-20 mt-2 w-80 rounded-lg border border-slate-200 bg-white py-1 shadow-md"
        >
          {list.data === undefined ? (
            <p className="px-3 py-4 text-sm text-slate-500">Cargando...</p>
          ) : list.data.items.length === 0 ? (
            <p className="px-3 py-4 text-sm text-slate-500">No tenés notificaciones.</p>
          ) : (
            <ul className="max-h-96 overflow-auto">
              {list.data.items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => void handleMarkRead(item.id)}
                    className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                      item.readAt === null ? 'bg-primary-50/50' : ''
                    }`}
                  >
                    <span className="text-slate-700">{item.body.replace(/<[^>]+>/g, '')}</span>
                    <span className="text-xs text-slate-400">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-slate-100 px-3 py-2">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-primary-600 hover:underline"
            >
              Ver todas
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
